import React, { useState, useRef, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";

const IMG_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/gallery-images`;
const img = (name) => `${IMG_BASE}/${name}.png`;

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] },
  }),
};

const BeforeAfter = ({
  title,
  subtitle,
  description,
  beforeSrc,
  afterSrc,
  beforeAlt = "Avant detailing",
  afterAlt = "Après detailing",
}) => {
  const [pos, setPos] = useState(50);
  const containerRef = useRef(null);
  const draggingRef = useRef(false);

  const updateFromClientX = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const rawPct = ((clientX - rect.left) / rect.width) * 100;
    const clamped = Math.min(100, Math.max(0, rawPct));
    setPos(clamped);
  }, []);

  const handlePointerDown = (e) => {
    draggingRef.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    updateFromClientX(e.clientX);
  };

  const handlePointerMove = (e) => {
    if (!draggingRef.current) return;
    updateFromClientX(e.clientX);
  };

  const handlePointerUp = (e) => {
    draggingRef.current = false;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  // Keyboard support (accessibility)
  const handleKeyDown = (e) => {
    if (e.key === "ArrowLeft") setPos((p) => Math.max(0, p - 5));
    if (e.key === "ArrowRight") setPos((p) => Math.min(100, p + 5));
  };

  // Nudge animation on first view — draws attention
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !draggingRef.current) {
            let start = null;
            const from = pos;
            const animate = (t) => {
              if (!start) start = t;
              const elapsed = t - start;
              if (elapsed < 400) {
                setPos(from + (35 - from) * (elapsed / 400));
                requestAnimationFrame(animate);
              } else if (elapsed < 900) {
                setPos(35 + (65 - 35) * ((elapsed - 400) / 500));
                requestAnimationFrame(animate);
              } else if (elapsed < 1400) {
                setPos(65 + (50 - 65) * ((elapsed - 900) / 500));
                requestAnimationFrame(animate);
              } else {
                setPos(50);
              }
            };
            requestAnimationFrame(animate);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.5 }
    );
    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section
      id="avant-apres"
      data-testid="before-after-section"
      className="relative py-24 md:py-40 px-6 md:px-12 bg-[#0a0a0a]"
    >
      <div className="max-w-[1500px] mx-auto grid md:grid-cols-12 gap-12 items-center">
        <div className="md:col-span-5">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <span className="text-[11px] uppercase tracking-[0.3em] text-[#D90000]">— {subtitle || "Avant / Après"}</span>
            <h2 className="font-impact text-5xl md:text-6xl lg:text-[80px] leading-[0.9] mt-6 mb-8">
              {title ? (
                <>
                  {title.first} <br />
                  <span className="text-[#D90000]">{title.second}</span>
                </>
              ) : (
                <>
                  LA PREUVE PAR <br />
                  <span className="text-[#D90000]">LE RÉSULTAT.</span>
                </>
              )}
            </h2>
            <p className="text-white/60 leading-relaxed mb-8 max-w-md">
              {description ||
                "Déplacez le curseur et découvrez la transformation. Chaque véhicule est traité avec la même exigence, du nettoyage au polissage jusqu'à la protection finale."}
            </p>
            <Link
              to="/galerie"
              data-testid="ba-gallery-cta"
              className="inline-flex items-center gap-3 border border-white/20 hover:border-[#D90000] hover:bg-[#D90000] text-white text-[12px] uppercase tracking-[0.25em] font-semibold px-8 py-4 transition-all"
            >
              Voir toute la galerie <ArrowUpRight size={14} />
            </Link>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="md:col-span-7"
        >
          <div
            ref={containerRef}
            data-testid="before-after-slider"
            role="slider"
            tabIndex={0}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(pos)}
            aria-label="Curseur avant/après"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            onKeyDown={handleKeyDown}
            className="relative aspect-[4/3] overflow-hidden select-none cursor-ew-resize touch-none group focus:outline-none focus:ring-2 focus:ring-[#D90000]"
            style={{ borderRadius: 0 }}
          >
            {/* After image (background — full) */}
            <img
              src={afterSrc}
              alt={afterAlt}
              draggable={false}
              className="absolute inset-0 w-full h-full object-cover pointer-events-none"
            />

            {/* Before image (foreground — clipped) */}
            <div
              className="absolute inset-0 overflow-hidden pointer-events-none"
              style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
            >
              <img
                src={beforeSrc}
                alt={beforeAlt}
                draggable={false}
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>

            {/* Divider line + handle */}
            <div
              className="absolute top-0 bottom-0 w-[3px] bg-[#D90000] pointer-events-none shadow-[0_0_20px_rgba(217,0,0,0.6)]"
              style={{ left: `${pos}%`, transform: "translateX(-50%)" }}
            >
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-14 h-14 md:w-16 md:h-16 bg-[#D90000] flex items-center justify-center shadow-[0_8px_30px_rgba(217,0,0,0.5)] group-hover:scale-110 transition-transform">
                <ChevronLeft size={16} className="text-white -mr-1" strokeWidth={3} />
                <ChevronRight size={16} className="text-white -ml-1" strokeWidth={3} />
              </div>
            </div>

            {/* Labels */}
            <div className="absolute top-4 left-4 px-3 py-1.5 bg-black/70 backdrop-blur-sm text-[10px] uppercase tracking-[0.3em] font-bold text-white pointer-events-none">
              Avant
            </div>
            <div className="absolute top-4 right-4 px-3 py-1.5 bg-[#D90000] text-[10px] uppercase tracking-[0.3em] font-bold text-white pointer-events-none">
              Après
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

/* Preset variants for each service context */
export const PolissageBeforeAfter = () => (
  <BeforeAfter
    subtitle="Polissage — Correction Peinture"
    title={{ first: "LA PREUVE PAR", second: "LE POLISH." }}
    description="Micro-rayures, hologrammes, oxydation : après passage à la polisseuse orbitale et aux compounds Koch Chemie, la peinture retrouve sa profondeur d'origine. Déplacez le curseur."
    beforeSrc={img("porsche_911_v5_before")}
    afterSrc={img("porsche_911_v5_after")}
    beforeAlt="Porsche 911 avec swirl marks et peinture oxydée avant polissage"
    afterAlt="Porsche 911 avec peinture polie miroir après VistaWash"
  />
);

export const NettoyageBeforeAfter = () => (
  <BeforeAfter
    subtitle="Nettoyage Premium"
    title={{ first: "LA PREUVE PAR", second: "LE DÉTAIL." }}
    description="Boue, projections routières, poussière de frein incrustée : nos protocoles de lavage manuel pH neutre et de décontamination redonnent au véhicule son éclat d'usine. Déplacez le curseur."
    beforeSrc={img("nettoyage_hero_before")}
    afterSrc={img("nettoyage_hero_after")}
    beforeAlt="Range Rover Sport couvert de boue avant nettoyage"
    afterAlt="Range Rover Sport noir miroir après nettoyage VistaWash"
  />
);

export default BeforeAfter;
